import { useContext, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { showAlertSessionInfo } from "../promptMessages/PromptMessages";
import { AuthContext } from "./AuthContextComponent";
import { ROUTES } from "../lib/enums/index";
import { ALERTMSG, ALERTICON } from "../lib/enums/index";
import { useCustomAccount } from "./CustomAccountContext";
import { useWebSocketNotifications } from "../hooks/WebSocketHook";
import { useBalance } from "./BalanceContext";

interface AutoLogoutProps {
  openOTP?: boolean;
  redirectModal?: boolean;
  redirectChangePassword?: boolean;
  isRejection?: boolean;
}

const requestIdleCallbackPolyfill = (
  cb: IdleRequestCallback,
  options?: IdleRequestOptions,
): number => {
  const start = Date.now();
  return window.setTimeout(() => {
    cb({
      didTimeout: false,
      timeRemaining: () => Math.max(0, 50 - (Date.now() - start)),
    });
  }, options?.timeout || 1);
};

const cancelIdleCallbackPolyfill = (id: number): void => {
  clearTimeout(id);
};

const requestIdle = window.requestIdleCallback || requestIdleCallbackPolyfill;
const cancelIdle = window.cancelIdleCallback || cancelIdleCallbackPolyfill;

export const AutoLogout: React.FC<AutoLogoutProps> = ({
  openOTP,
  redirectModal,
  redirectChangePassword,
  isRejection,
}) => {
  const { setLoggedIn, loggedIn } = useContext(AuthContext);
  const { customAccount } = useCustomAccount();
  const navigate = useNavigate();
  const location = useLocation();
  const customColor = customAccount?.customAccountInfo[0].themeCode;
  const { disconnectSocket } = useWebSocketNotifications();
  const { clearBalances } = useBalance();

  const idleCallbackIdRef = useRef<number | null>(null);
  const logoutTimerIdRef = useRef<number | null>(null);
  const lastActivityTimeRef = useRef<number>(Date.now());

  const handleLogout = (): void => {
    if (loggedIn) {
      setLoggedIn(false, "");
      disconnectSocket();
      clearBalances();
      sessionStorage.clear();

      navigate(ROUTES.LOGIN);

      showAlertSessionInfo(
        ALERTMSG.SESSIONEXP,
        ALERTMSG.SESSIONEXPMSG,
        ALERTICON.WARN,
        "",
        customColor,
      );
    }
  };

  useEffect(() => {
    if (!loggedIn && location.pathname !== ROUTES.LOGIN) {
      setLoggedIn(true, "");
    }
  }, [loggedIn, location, setLoggedIn]);

  useEffect(() => {
    let rafId: number | null = null;

    const resetActivityTimer = () => {
      lastActivityTimeRef.current = Date.now();
    };

    const autoLogout = (deadline: IdleDeadline) => {
      if (openOTP || redirectModal || redirectChangePassword || isRejection)
        return;

      const now = Date.now();
      const inactivityDuration = now - lastActivityTimeRef.current;

      if (inactivityDuration >= 2 * 60 * 1000) {
        handleLogout();
      } else {
        if (logoutTimerIdRef.current !== null) {
          clearTimeout(logoutTimerIdRef.current);
        }
        const timeoutId = window.setTimeout(
          handleLogout,
          2 * 60 * 1000 - inactivityDuration,
        );
        logoutTimerIdRef.current = timeoutId;
      }

      idleCallbackIdRef.current = requestIdle(autoLogout, { timeout: 2000 });
    };

    const handleUserActivity = () => {
      resetActivityTimer();
      if (rafId === null) {
        rafId = requestAnimationFrame(() => {
          rafId = null;
          if (idleCallbackIdRef.current !== null) {
            cancelIdle(idleCallbackIdRef.current);
          }
          idleCallbackIdRef.current = requestIdle(autoLogout, {
            timeout: 2000,
          });
        });
      }
    };

    document.addEventListener("mousemove", handleUserActivity);
    document.addEventListener("keydown", handleUserActivity);
    document.addEventListener("scroll", handleUserActivity);
    document.addEventListener("visibilitychange", handleUserActivity);

    idleCallbackIdRef.current = requestIdle(autoLogout, { timeout: 2000 });

    return () => {
      document.removeEventListener("mousemove", handleUserActivity);
      document.removeEventListener("keydown", handleUserActivity);
      document.removeEventListener("scroll", handleUserActivity);
      document.removeEventListener("visibilitychange", handleUserActivity);

      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }

      if (logoutTimerIdRef.current !== null) {
        clearTimeout(logoutTimerIdRef.current);
      }

      if (idleCallbackIdRef.current !== null) {
        cancelIdle(idleCallbackIdRef.current);
      }
    };
  }, [openOTP, redirectModal, redirectChangePassword, isRejection]);

  return null;
};
